import { useState } from 'react'
import { FormikProvider, useFormik } from 'formik'

import {
  UPDATE_USER_PASSWORD_KEY,
  updateUserPassword,
  VERIFY_PASSWORD_KEY,
  verifyPassword,
} from '@/actions/authActions'
import { selectErrorsForKeys } from '@/reducers/fetch'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { getFieldName, makePasswordSchema, makeReqStringSchema } from '@/base'
import { Alert, Button, Dialog, Form, StepDialog } from '@/ui'

const ChangePasswordSuccess = ({ onClose }: { onClose: () => void }) => (
  <>
    <Alert variant="success">
      Your password has been updated successfully.
    </Alert>
    <Dialog.Footer className="mt-6">
      <Button onClick={onClose}>Close</Button>
    </Dialog.Footer>
  </>
)

interface ChangePasswordFormProps {
  errors: { message: string }[]
  formik: ReturnType<
    typeof useFormik<{
      password: string
      newPassword: string
      newPasswordConfirm: string
    }>
  >
  onClose: () => void
}

const ChangePasswordForm = ({
  errors,
  formik,
  onClose,
}: ChangePasswordFormProps) => (
  <>
    {Boolean(errors.length) && (
      <Alert variant="error">{errors[0].message}</Alert>
    )}

    <Dialog.Description>
      Use at least 8 characters. Don&apos;t use a password from another site,
      and don&apos;t include any personal information such as your name or date
      of birth.
    </Dialog.Description>

    <FormikProvider value={formik}>
      <Form.Input
        name={getFieldName<typeof formik.values>('password')}
        label="Current Password"
        required
        schema={makeReqStringSchema()}
        type="password"
      />

      <Form.Input
        name={getFieldName<typeof formik.values>('newPassword')}
        label="New Password"
        description="Your password needs to have more than 8 characters, and must include a number and a symbol."
        required
        schema={makePasswordSchema()}
        type="password"
      />

      <Form.Input
        name={getFieldName<typeof formik.values>('newPasswordConfirm')}
        label="Confirm New Password"
        required
        schema={makeReqStringSchema({
          field: 'confirm password',
        }).test(
          'Password Test',
          'Passwords do not match',
          (confirmPassword) => confirmPassword === formik.values.newPassword
        )}
        type="password"
      />

      <Dialog.Footer className="mt-6">
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
        <Button disabled={!formik.isValid} onClick={formik.submitForm}>
          Change Password
        </Button>
      </Dialog.Footer>
    </FormikProvider>
  </>
)

export const ChangePassword = () => {
  const dispatch = useAppDispatch()
  const [open, setOpen] = useState(false)
  const [activeStep, setActiveStep] = useState(0)

  const errors = useReselector(selectErrorsForKeys, [
    VERIFY_PASSWORD_KEY,
    UPDATE_USER_PASSWORD_KEY,
  ])

  const formik = useFormik({
    initialValues: {
      password: '',
      newPassword: '',
      newPasswordConfirm: '',
    },
    onSubmit: async ({ password, newPassword }) => {
      const passwordResponse = await verifyPassword({ password })(dispatch)
      if (!passwordResponse) return

      const updatePasswordResponse = await updateUserPassword({ newPassword })(
        dispatch
      )
      if (updatePasswordResponse) setActiveStep(1)
    },
  })

  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    if (!next) {
      formik.resetForm()
      setActiveStep(0)
    }
  }

  const steps = [
    {
      content: (
        <ChangePasswordForm
          errors={errors}
          formik={formik}
          onClose={() => handleOpenChange(false)}
        />
      ),
    },
    {
      content: (
        <ChangePasswordSuccess onClose={() => handleOpenChange(false)} />
      ),
    },
  ]

  return (
    <StepDialog
      open={open}
      onOpenChange={handleOpenChange}
      trigger={
        <Button variant="secondary" className="w-full md:w-auto h-full">
          Change password
        </Button>
      }
      title="Change Your Password"
      steps={steps}
      activeStep={activeStep}
    />
  )
}
